import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '@prio365/prio365-react-library';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import Flex from '../../../../../../components/Flex';
import { PrioTheme } from '../../../../../../theme/types';
import { makePrioStyles } from '../../../../../../theme/utils';
import {
  EIncomingInvoiceState,
  IncomingInvoiceDto,
} from '../../../../../../models/IncomingInvoices';
import IncomingInvoiceStatusPill from './IncomingInvoiceStatusPill';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    width: '100%',
    padding: theme.spacing.regular,
    backgroundColor: theme.colors.application.background.light,
    borderRadius: theme.borderRadius.regular,
    borderTopLeftRadius: 0,
  },
  smallText: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  noteText: {
    fontSize: '12px',
    color: '#999',
    fontStyle: 'italic',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface IncomingInvoicesWidgetCardProps {
  className?: string;
  invoice: IncomingInvoiceDto;
  handleOpenInvoice: (invoice: IncomingInvoiceDto) => void;
  handleDeleteInvoice?: (invoice: IncomingInvoiceDto) => void;
}

export const IncomingInvoicesWidgetCard: React.FC<
  IncomingInvoicesWidgetCardProps
> = ({ className, invoice, handleOpenInvoice, handleDeleteInvoice }) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const isProcessing =
    invoice.incomingInvoiceState === EIncomingInvoiceState.Processing;

  const iconProp: IconProp = useMemo(() => {
    if (isProcessing) {
      return ['fal', 'eye'] as IconProp;
    }
    switch (invoice.incomingInvoiceState) {
      case EIncomingInvoiceState.Draft:
      case EIncomingInvoiceState.MissingFile:
        return ['fal', 'pen'] as IconProp;
      case EIncomingInvoiceState.ApprovalRequested:
        return ['fal', 'eye'] as IconProp;
      default:
        return ['fal', 'eye'] as IconProp;
    }
  }, [invoice.incomingInvoiceState, isProcessing]);

  const displayInvoiceNumber =
    invoice.invoiceNumber || invoice.originalAnalyzationResult?.invoiceNumber;
  const displayTotal =
    invoice.totalIncludingVat === 0 &&
    invoice.totalIncludingVat !==
      invoice.originalAnalyzationResult?.totalIncludingVat
      ? invoice.originalAnalyzationResult?.totalIncludingVat?.toLocaleString(
          undefined,
          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        )
      : invoice.totalIncludingVat?.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  const displayCurrency =
    invoice.currency || invoice.originalAnalyzationResult?.currency;
  //#endregion

  return (
    <div className={classNames(classes.root, className)}>
      <Flex.Row alignItems="center" justifyContent="space-between">
        <Flex.Column childrenGap={4} style={{ flex: 1 }}>
          {!isProcessing && (
            <Flex.Row childrenGap={8} style={{ alignItems: 'center' }}>
              {displayInvoiceNumber && displayTotal ? (
                <>
                  <span
                    className={`${classes.smallText} ${classes.ellipsis}`}
                    style={{ maxWidth: '125px' }}
                  >
                    {displayInvoiceNumber}
                  </span>
                  <span className={classes.smallText}>
                    | {displayTotal} {displayCurrency}
                  </span>
                </>
              ) : (
                <span className={classes.smallText}>
                  {t('accounting:incomingInvoices.widgetCard.missingData')}
                </span>
              )}
            </Flex.Row>
          )}

          <Flex.Row childrenGap={8} alignItems="center">
            <IncomingInvoiceStatusPill invoice={invoice} />
            {invoice.notes && (
              <span className={classes.noteText}>{invoice.notes}</span>
            )}
          </Flex.Row>
        </Flex.Column>

        <Flex.Row childrenGap={theme.spacing.extraSmall}>
          <Button
            type="link"
            iconProp={iconProp}
            size="default"
            onClick={() => handleOpenInvoice(invoice)}
          />
          {handleDeleteInvoice &&
            (invoice.incomingInvoiceState === EIncomingInvoiceState.Draft ||
              invoice.incomingInvoiceState ===
                EIncomingInvoiceState.MissingFile) && (
              <Button
                type="link"
                iconProp={['fal', 'trash']}
                size="default"
                onClick={() => handleDeleteInvoice(invoice)}
              />
            )}
        </Flex.Row>
      </Flex.Row>
    </div>
  );
};

export default IncomingInvoicesWidgetCard;
